import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronRight,
  Clock,
  KeyRound,
  Share2,
  ShoppingBag,
  TrendingUp,
  X,
} from 'lucide-react';
import { Button, Card, CardContent } from '@mlm/ui';
import { getOnboarding, type OnboardingStage } from '../lib/api';

/**
 * BF1 onboarding spine. A stateful "one next step" card that reads the member's
 * activation rung from `/me/onboarding` and always points at the single most
 * valuable next action. Dismissible to a slim strip (persisted), and hidden
 * entirely once the member is "rolling" (active slot + at least one lead).
 *
 * Trustworthy/calm by design: one sky-blue primary action, quiet progress,
 * no hype. See PRODUCT.md "One next step, always".
 */

const DISMISS_KEY = 'member.onboardingDismissed';

const STEPS = [
  { key: 'kit', label: 'Get kit', icon: ShoppingBag },
  { key: 'activate', label: 'Activate', icon: KeyRound },
  { key: 'invite', label: 'Invite', icon: Share2 },
  { key: 'earn', label: 'Earn', icon: TrendingUp },
] as const;

type Action = {
  title: string;
  body: string;
  cta: string;
  to: string;
  icon: typeof ShoppingBag;
  short: string;
};

function stageToStep(stage: OnboardingStage): number {
  switch (stage) {
    case 'registered':
    case 'payment_pending':
      return 0;
    case 'code_in_vault':
      return 1;
    case 'activated':
      return 2;
    case 'rolling':
      return STEPS.length; // all complete
  }
}

function nextAction(stage: OnboardingStage): Action {
  switch (stage) {
    case 'registered':
      return {
        title: 'Get your starter kit',
        body: 'Buy a Kit from the Store. It comes with an activation Code and PIN that turns on your first slot.',
        cta: 'Browse kits',
        to: '/dashboard',
        icon: ShoppingBag,
        short: 'Buy a starter kit',
      };
    case 'payment_pending':
      return {
        title: 'Payment under review',
        body: 'Your kit order is being reviewed. Once it is approved, your Code and PIN will appear in your Vault to redeem.',
        cta: 'View order',
        to: '/orders',
        icon: Clock,
        short: 'Kit payment under review',
      };
    case 'code_in_vault':
      return {
        title: 'Activate your slot',
        body: 'You have an unredeemed Code and PIN in your Vault. Redeem it to activate your first slot and join the tree.',
        cta: 'Open Vault',
        to: '/code-vault',
        icon: KeyRound,
        short: 'Redeem your code to activate',
      };
    case 'activated':
      return {
        title: 'Invite your first member',
        body: 'Your slot is live. Share your link to start building your team. Your first referral is the milestone that gets things moving.',
        cta: 'Share invite link',
        to: '/invite',
        icon: Share2,
        short: 'Invite your first member',
      };
    case 'rolling':
      return { title: '', body: '', cta: '', to: '/dashboard', icon: TrendingUp, short: '' };
  }
}

export function ActivationLadder() {
  const { data, isLoading } = useQuery({ queryKey: ['onboarding'], queryFn: getOnboarding });
  const [dismissed, setDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1',
  );

  if (isLoading || !data) return null;
  // Fully onboarded members never see the ladder.
  if (data.stage === 'rolling') return null;

  const step = stageToStep(data.stage);
  const action = nextAction(data.stage);

  function dismiss() {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, '1');
  }
  function reopen() {
    setDismissed(false);
    localStorage.removeItem(DISMISS_KEY);
  }

  if (dismissed) {
    return (
      <button
        onClick={reopen}
        className="mb-4 flex w-full items-center justify-between rounded-lg border bg-card px-4 py-2.5 text-left text-sm shadow-card transition-colors hover:bg-muted/50"
      >
        <span className="text-muted-foreground">
          <span className="font-medium text-foreground">Getting started</span> · {action.short}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2.5} />
      </button>
    );
  }

  const NextIcon = action.icon;

  return (
    <Card className="mb-4 border-primary/20 bg-primary/5">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Getting started
            </div>
            <h2 className="text-h2 mt-1">{action.title}</h2>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss getting started"
            className="-mr-1 -mt-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Quiet progress rail: Kit -> Activate -> Invite -> Earn */}
        <ol className="mt-5 flex items-center">
          {STEPS.map((s, i) => {
            const done = i < step;
            const current = i === step;
            const Icon = done ? Check : s.icon;
            return (
              <li key={s.key} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-center gap-1.5">
                  <span
                    className={
                      'flex h-9 w-9 items-center justify-center rounded-full border transition-colors ' +
                      (done
                        ? 'border-primary bg-primary text-primary-foreground'
                        : current
                          ? 'border-primary text-primary'
                          : 'border-border text-muted-foreground')
                    }
                  >
                    <Icon className="h-4 w-4" strokeWidth={done ? 3 : 2} />
                  </span>
                  <span
                    className={
                      'text-[11px] ' +
                      (current ? 'font-medium text-foreground' : 'text-muted-foreground')
                    }
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <span
                    className={
                      'mx-1 mb-5 h-px flex-1 transition-colors ' +
                      (i < step ? 'bg-primary' : 'bg-border')
                    }
                  />
                )}
              </li>
            );
          })}
        </ol>

        <p className="mt-4 text-sm text-muted-foreground">{action.body}</p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button asChild size="sm">
            <Link to={action.to}>
              <NextIcon className="h-4 w-4" /> {action.cta}
            </Link>
          </Button>
          <button
            onClick={dismiss}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            I'll explore on my own
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
